import React, { useState, useEffect } from "react";
import { Modal } from "../Modal";
import { useToast } from "../Toast";
import { GovRegAgency } from "../../types/legalOps";
import { 
    listGovAgencies, 
    createGovAgency, 
    updateGovAgency, 
    deleteGovAgency,
    LOCKED_AGENCIES,
    resetLockedAgencyBaseUrls
} from "../../services/govRegWatchService";

interface Props {
    onClose: () => void;
    onUpdate: () => void;
}

export function TrackedAuthoritiesDialog({ onClose, onUpdate }: Props) {
    const { showToast } = useToast();
    const [agencies, setAgencies] = useState<GovRegAgency[]>([]);
    const [loading, setLoading] = useState(false);
    
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState({ id: "", name: "", shortName: "", baseUrl: "", enabled: true });

    const fetchList = async () => {
        setLoading(true);
        try {
            const list = await listGovAgencies(true);
            setAgencies(list);
        } catch (e) {
            console.error(e);
            showToast("讀取清單失敗", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchList(); }, []);

    const resetForm = () => {
        setForm({ id: "", name: "", shortName: "", baseUrl: "", enabled: true });
        setIsEditing(false);
    };

    const handleEdit = (agency: GovRegAgency) => {
        setForm({
            id: agency.id,
            name: agency.name,
            shortName: agency.shortName || "",
            baseUrl: agency.baseUrl || "",
            enabled: agency.enabled !== false
        });
        setIsEditing(true);
    };

    const handleDelete = async (id: string, name: string) => {
        if (!confirm(`確定刪除「${name}」？\n刪除後將停止相關爬蟲任務。`)) return;
        try {
            await deleteGovAgency(id);
            showToast("已刪除");
            fetchList();
            onUpdate();
        } catch (e: any) {
            showToast(e.message, "error");
        }
    };

    const handleSubmit = async () => {
        if (!form.id || !form.name || !form.shortName) {
            alert("請填寫 ID, 完整名稱與簡稱");
            return;
        }

        try {
            if (isEditing) {
                await updateGovAgency(form.id, form);
                showToast("已更新");
            } else {
                // Basic check
                if (agencies.some(a => a.id === form.id)) {
                    alert("ID 已存在");
                    return;
                }
                await createGovAgency(form as any);
                showToast("已新增");
            }
            resetForm();
            fetchList();
            onUpdate();
        } catch (e: any) {
            console.error(e);
            showToast("儲存失敗: " + e.message, "error");
        }
    };

    const isLocked = (id: string) => LOCKED_AGENCIES.some(l => l.id === id);

    const handleResetSystemDefaults = async () => {
        if (!confirm("確定要『一鍵重設』系統預設主管機關的公告網址？\n\n- 只會覆蓋系統預設（★）的 baseUrl\n- 不會刪除或改動你自建的來源\n- 若曾被刪除（tombstone），會一併復活")) return;
        setLoading(true);
        try {
            await resetLockedAgencyBaseUrls();
            showToast("已重設系統預設公告網址（保留自訂）");
            await fetchList();
            onUpdate();
        } catch (e: any) {
            console.error(e);
            showToast("重設失敗：" + (e?.message || "unknown"), "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal title="設定爬蟲來源 / 主管機關 (Crawler Config)" onClose={onClose} width={850}>
            {/* Top actions */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10}}>
                <div style={{fontSize: 12, color: '#9ca3af'}}>
                    ★ 系統預設：可手動改；需要時可用「一鍵重設」恢復預設公告清單網址。
                </div>
                <button
                    className="smallbtn"
                    style={{background:'#fff', color:'#000', fontWeight:700}}
                    onClick={handleResetSystemDefaults}
                    disabled={loading}
                >
                    一鍵重設系統預設公告網址（保留自訂）
                </button>
            </div>

            {/* Input Form */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1.5fr 0.8fr 1.5fr auto', gap: 10, marginBottom: 20, alignItems: 'end', background: 'rgba(255,255,255,0.03)', padding: 16, borderRadius: 8, border:'1px solid #333'}}>
                <div className="field" style={{marginTop:0}}>
                    <label className="label">ID (Slug) *</label>
                    <input className="input" placeholder="e.g. new-agency" value={form.id} onChange={e => setForm({...form, id: e.target.value})} disabled={isEditing} />
                </div>
                <div className="field" style={{marginTop:0}}>
                    <label className="label">機關全銜 *</label>
                    <input className="input" placeholder="e.g. 數位發展部" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                </div>
                <div className="field" style={{marginTop:0}}>
                    <label className="label">簡稱 *</label>
                    <input className="input" placeholder="e.g. 數發部" value={form.shortName} onChange={e => setForm({...form, shortName: e.target.value})} />
                </div>
                <div className="field" style={{marginTop:0}}>
                    <label className="label">網址 (Base URL)</label>
                    <textarea
                        className="input"
                        placeholder="https://...（請貼公告清單頁，不要貼官網首頁）"
                        value={form.baseUrl}
                        onChange={e => setForm({...form, baseUrl: e.target.value})}
                        rows={2}
                        style={{width:'100%', resize:'vertical', lineHeight:1.35, whiteSpace:'pre-wrap'}}
                    />
                </div>
                <div style={{paddingBottom: 4, display:'flex', gap:6}}>
                    <button className="smallbtn" style={{background: isEditing ? '#fbbf24' : '#fff', color: '#000', fontWeight:'bold'}} onClick={handleSubmit}>
                        {isEditing ? "更新" : "新增"}
                    </button>
                    {isEditing && <button className="smallbtn" onClick={resetForm}>取消</button>}
                </div>
            </div>

            {/* List */}
            <div style={{flex: 1, overflow: 'auto', border: '1px solid #333', borderRadius: 8}}>
                <table style={{width: '100%', fontSize: 13, borderCollapse: 'collapse'}}>
                    <thead style={{background: '#222', color: '#aaa', textAlign: 'left'}}>
                        <tr>
                            <th style={{padding: 10}}>ID / 類別</th>
                            <th style={{padding: 10}}>簡稱</th>
                            <th style={{padding: 10}}>全銜 & 網址</th>
                            <th style={{padding: 10}}>狀態</th>
                            <th style={{padding: 10, textAlign: 'right'}}>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} style={{padding:20, textAlign:'center'}}>載入中...</td></tr>}
                        {!loading && agencies.map(a => {
                            const locked = isLocked(a.id);
                            return (
                                <tr key={a.id} style={{borderBottom: '1px solid #333', background: locked ? 'rgba(59, 130, 246, 0.03)' : 'transparent'}}>
                                    <td style={{padding: 10}}>
                                        <div style={{fontFamily: 'monospace', color: '#a5b4fc'}}>{a.id}</div>
                                        {locked && <span style={{fontSize:10, color:'#fbbf24'}}>★ 系統設定</span>}
                                    </td>
                                    <td style={{padding: 10, fontWeight: 'bold'}}>{a.shortName}</td>
                                    <td style={{padding: 10}}>
                                        <div>{a.name}</div>
                                        <div style={{fontSize:11, color:'#666', wordBreak:'break-all', overflowWrap:'anywhere'}}>{a.baseUrl}</div>
                                        {a.note && <div style={{fontSize:11, color:'#fbbf24', marginTop:2}}>{a.note}</div>}
                                    </td>
                                    <td style={{padding: 10}}>
                                        <button 
                                            className="badge"
                                            style={{
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                wordBreak: 'keep-all',
                                                overflowWrap: 'normal',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 92,
                                                lineHeight: 1,
                                                textAlign: 'center',
                                                background: a.enabled !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.1)',
                                                color: a.enabled !== false ? '#34d399' : '#aaa',
                                                border: '1px solid transparent'
                                            }}
                                            onClick={async () => {
                                                await updateGovAgency(a.id, { enabled: !(a.enabled !== false) });
                                                fetchList();
                                                onUpdate();
                                            }}
                                        >
                                            {a.enabled !== false ? "●啟用中" : "○已停用"}
                                        </button>
                                    </td>
                                    <td style={{padding: 10, textAlign: 'right'}}>

                                        <div style={{display:'flex', justifyContent:'flex-end', gap:8, flexWrap:'nowrap'}}>
                                            <button className="smallBtn" style={{whiteSpace:'nowrap'}} onClick={() => handleEdit(a)}>編輯</button>
                                            <button className="smallBtn" style={{whiteSpace:'nowrap', background:'rgba(239, 68, 68, 0.12)', color:'#fecaca', border:'1px solid rgba(239, 68, 68, 0.35)'}} onClick={() => handleDelete(a.id, a.name)}>刪除</button>
                                        </div>

                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
}
